import React, { useState, useEffect, useMemo } from 'react';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import ErrorMessage from '../components/ui/ErrorMessage';
import EmptyState from '../components/ui/EmptyState';
import './About.css';

const CommitteeSection = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let mounted = true;
    fetch('/api/committee')
      .then(res => {
        if (!res.ok) throw new Error('Failed to load committee');
        return res.json();
      })
      .then(data => { if (mounted) setMembers(Array.isArray(data) ? data : []); })
      .catch(e => { if (mounted) setError(e.message); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  if (loading) return <LoadingSpinner />;

  return (
    <>
      {error && <ErrorMessage message={error} />}
      {!error && members.length === 0 && <EmptyState message="Committee details coming soon." />}

      <div className="committee-grid-new">
        {members.map((member, i) => (
          <article
            className="card member-card"
            key={i}
            onClick={() => setSelected(member)}
            role="button"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && setSelected(member)}
          >
            <div className="member-avatar">
              <img src={member.image || 'https://via.placeholder.com/150'} alt={member.name} />
            </div>
            <h3>{member.name}</h3>
            <div className="member-role">{member.role}</div>
          </article>
        ))}
      </div>

      {selected && (
        <div className="modal-overlay-new" onClick={() => setSelected(null)}>
          <div className="modal-new" onClick={e => e.stopPropagation()}>
            <button className="modal-close-new" onClick={() => setSelected(null)} aria-label="Close">×</button>
            <div className="modal-avatar">
              <img src={selected.image || 'https://via.placeholder.com/150'} alt={selected.name} />
            </div>
            <span className="badge"><span className="badge-dot"></span>{selected.role}</span>
            <h2 style={{ margin: '12px 0 8px' }}>{selected.name}</h2>
            {selected.fullBio && <p className="muted">{selected.fullBio}</p>}
            {selected.description && !selected.fullBio && <p className="muted">{selected.description}</p>}
          </div>
        </div>
      )}
    </>
  );
};

const DocumentsSection = () => {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All');
  const [year, setYear] = useState('All');
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    fetch('/api/documents')
      .then(res => {
        if (!res.ok) throw new Error('Failed to load documents');
        return res.json();
      })
      .then(data => { if (mounted) setDocs(Array.isArray(data) ? data : []); })
      .catch(e => { if (mounted) setError(e.message); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const categories = useMemo(() => {
    const s = new Set(docs.map(d => d.category).filter(Boolean));
    return ['All', ...Array.from(s).sort()];
  }, [docs]);

  const years = useMemo(() => {
    const s = new Set(docs.map(d => d.year).filter(Boolean));
    return ['All', ...Array.from(s).sort((a, b) => b - a)];
  }, [docs]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return docs
      .filter(d => category === 'All' || d.category === category)
      .filter(d => year === 'All' || String(d.year) === String(year))
      .filter(d => !q || `${d.title} ${d.category} ${d.year}`.toLowerCase().includes(q))
      .sort((a, b) => {
        const ap = a.published ? new Date(a.published).getTime() : 0;
        const bp = b.published ? new Date(b.published).getTime() : 0;
        return bp !== ap ? bp - ap : (b.year || 0) - (a.year || 0);
      });
  }, [query, category, year, docs]);

  if (loading) return <LoadingSpinner />;

  return (
    <>
      {error && <ErrorMessage message={error} />}

      <div className="docs-filters">
        <input
          type="text"
          placeholder="Search documents…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          className="docs-search"
        />
        <select value={category} onChange={e => setCategory(e.target.value)}>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={year} onChange={e => setYear(e.target.value)}>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {filtered.length === 0
        ? <EmptyState message="No documents available yet." />
        : (
          <div className="doc-list-new">
            {filtered.map(d => (
              <div className="card doc-row" key={`${d.title}-${d.fileUrl}`}>
                <div className="doc-ic-new">PDF</div>
                <div className="doc-main-new">
                  <div className="doc-title-new">{d.title}</div>
                  <div className="doc-meta-new">
                    {d.category && <span>{d.category}</span>}
                    {d.category && d.year && <span>·</span>}
                    {d.year && <span>{d.year}</span>}
                    {d.published && <><span>·</span><span>Published {d.published}</span></>}
                  </div>
                </div>
                <div className="row" style={{ gap: 8 }}>
                  <a href={d.fileUrl} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">Open</a>
                  <a href={d.fileUrl} download className="btn btn-ghost btn-sm">Download</a>
                </div>
              </div>
            ))}
          </div>
        )
      }
      <p className="muted" style={{ marginTop: 16, fontSize: '0.85rem' }}>
        If a document doesn't open, please try downloading it instead.
      </p>
    </>
  );
};

const About = () => (
  <div className="container">
    <section className="about-preamble">
      <span className="kicker">Who we are</span>
      <h1>About CWARA</h1>
      <p className="lead">
        The Carlisle and Waverley Area Residents Association has been bringing neighbours
        together for many years. We are a volunteer-run committee working to make our
        area a friendly, safe and connected place to live.
      </p>
      <p className="muted" style={{ marginTop: 12 }}>
        Our primary means of communication is a regular email update. This website acts
        as a reference point — a place to find documents, meet the committee, and join
        our mailing list.
      </p>
    </section>

    <section className="about-section">
      <div className="sec-head">
        <span className="kicker">Meet the committee</span>
        <h2>The neighbours who make it happen</h2>
        <p className="lead">Click on any member to read a little more.</p>
      </div>
      <CommitteeSection />
    </section>

    <section className="about-section">
      <div className="sec-head">
        <span className="kicker">Papers &amp; minutes</span>
        <h2>Documents</h2>
        <p className="lead">
          Meeting minutes, the constitution, AGM papers and reports — all in one place.
        </p>
      </div>
      <DocumentsSection />
    </section>
  </div>
);

export default About;
